import { InputAdornment } from '@mui/material';
import MuiTextField, { TextFieldProps } from '@mui/material/TextField';

import { debounce } from 'lodash';
import {
  Controller,
  ControllerProps,
  FieldError,
  FieldValues,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  useFormContext
} from 'react-hook-form';
import {
  CepMask,
  CurrencyMask,
  CurrencyMask20Decimal,
  CurrencyMask6Decimal,
  DigitCodeMax4,
  DocumentMask,
  LatLngMask,
  NumberMask,
  PhoneMask
} from './mask';

type MaskTypes =
  | 'currency'
  | '4DigitCode'
  | 'currency6Decimal'
  | 'currency20Decimal'
  | 'number'
  | 'phone'
  | 'document'
  | 'latLng'
  | 'cep';

export interface ITextFieldChangeProps {
  value: string;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
}

interface ITextFieldProps {
  name: string;
  label: string;
  autoFocus?: boolean;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: HTMLInputElement['autocomplete'];
  mask?: MaskTypes;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  shrink?: boolean;
  disabled?: boolean;
  maxLength?: number;
  toUpperCase?: boolean;
  onChange?: (props: ITextFieldChangeProps) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  helperText?: React.ReactNode;
  color?: TextFieldProps['color'];
  size?: TextFieldProps['size'];
  variant?: TextFieldProps['variant'];
  minValue?: number; // Adiciona o parâmetro de valor mínimo
  defaultValue?: ControllerProps['defaultValue'];
}

export function TextFieldRHF({
  name,
  label,
  autoComplete,
  mask,
  onChange: onChangeValue,
  minValue,
  defaultValue,
  ...rest
}: ITextFieldProps) {
  const { control, getValues, setValue, clearErrors } = useFormContext();
  const { startAdornment, endAdornment, ...restTextField } = rest;

  function getErrorMessage(error: FieldError) {
    const message = error.message ?? '';
    if (!message || message.toLowerCase().includes('obrigat')) return undefined;

    return `${label} ${message}`;
  }

  const getMask = {
    currency: CurrencyMask,
    '4DigitCode': DigitCodeMax4,
    currency6Decimal: CurrencyMask6Decimal,
    currency20Decimal: CurrencyMask20Decimal,
    number: NumberMask,
    phone: PhoneMask,
    document: DocumentMask,
    latLng: LatLngMask,
    cep: CepMask
  };

  const onChangeDebounced = debounce((value: string) => {
    const numericValue = parseFloat(value);
    if (minValue !== undefined && numericValue < minValue) {
      return; // Se o valor for menor que o minValue, não faz nada
    }
    if (onChangeValue) onChangeValue({ value, setValue, getValues, clearErrors });
  }, 300);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <MuiTextField
          {...field}
          label={label}
          error={Boolean(error)}
          fullWidth
          autoComplete={autoComplete}
          InputProps={{
            inputComponent: mask ? (getMask[mask] as any) : undefined,
            inputProps: {
              onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                if (onChangeValue) onChangeDebounced(event.target.value);
                field.onChange(event);
              },
              maxLength: rest.maxLength || 255,
              style: { textTransform: rest.toUpperCase ? 'uppercase' : 'none' },
              min: minValue, // Adiciona o atributo min ao input
              onKeyDown: rest.onKeyDown
            },
            startAdornment: startAdornment ? (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ) : undefined,
            endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined
          }}
          InputLabelProps={{
            shrink: rest.shrink
          }}
          color={rest.color}
          sx={{
            display: rest.type === 'hidden' ? 'none' : 'inherit'
          }}
          {...restTextField}
          helperText={error ? getErrorMessage(error) ?? rest.helperText : rest.helperText}
        />
      )}
    />
  );
}
